import {Box, Stack} from "@mui/material";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyRedactionField} from "crqlar-react-common/dist/models/survey/surveyRedaction.model";
import {SurveyAnswer} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {convertLocalDateToDate, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import React, {useMemo} from "react";
import {SurveyAnswerMedia} from "./SurveyAnswerMedia";
import {surveyAnswerMediaLinks} from "./surveyAnswerMedia.util";
import {surveyMarkdownToPlainText} from "./surveyMarkdownText.util";
import {getSurveyQuestionTypeFacts, isScreenType} from "./surveyQuestionTypeFacts";

export type SurveyAnswerContext = {
    localize: (text?: LocalizedText) => string | undefined
    t: (key: string) => string
}

export const useSurveyAnswerContext = (): SurveyAnswerContext => {
    const {t, localize} = useLocalize()

    return useMemo<SurveyAnswerContext>(
        () => ({localize, t: (key: string) => t(key)}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t]
    )
}

export const isAnswerableQuestion = (question: SurveyQuestion) => !isScreenType(question.type);

/**
 * The questions submitted answers are shown against: every answerable one, except a switched-off question none of
 * these answers reached. Guests stopped getting it, so a "Not answered" row would only be noise, while an answer it
 * collected before it was switched off stays in view. Takes one submission's answers, or every exported row's.
 */
export const submissionQuestions = (questions: SurveyQuestion[] | undefined, answers: SurveyAnswer[] | undefined) => {
    const answered = new Set((answers ?? []).map(answer => answer.questionId));

    return (questions ?? []).filter(question => (
        isAnswerableQuestion(question) && (!question.switchedOff || answered.has(question.id))
    ));
};

export const surveyQuestionTitle = (question: SurveyQuestion, ctx: SurveyAnswerContext) => (
    surveyMarkdownToPlainText(ctx.localize(question.title))?.trim() || question.id || ''
);

export type SurveyAnswerTextPart = {
    field?: SurveyRedactionField
    text: string
    leadingTrim: number
}

type AnswerParts = {
    parts: SurveyAnswerTextPart[]
    mediaCount: number
    hasSignature: boolean
}

const plainPart = (text: string): SurveyAnswerTextPart => ({text, leadingTrim: 0});

const fieldPart = (raw: string, field: SurveyRedactionField): SurveyAnswerTextPart => ({
    field,
    text: raw.trim(),
    leadingTrim: raw.length - raw.trimStart().length,
});

// "1 / 10": the answer's own type first, because the draft may have changed the question's type since.
const ratingPart = (value: number, answer: SurveyAnswer, question: SurveyQuestion | undefined) => {
    const scale = getSurveyQuestionTypeFacts(answer.type)?.scale ?? getSurveyQuestionTypeFacts(question?.type)?.scale;
    return plainPart(scale ? `${value} / ${scale.max}` : String(value));
};

export const surveyAnswerParts = (
    answer: SurveyAnswer | undefined,
    question: SurveyQuestion | undefined,
    ctx: SurveyAnswerContext
): AnswerParts => {
    const parts: SurveyAnswerTextPart[] = [];

    if (!answer) {
        return {parts, mediaCount: 0, hasSignature: false};
    }

    if (answer.optionIds?.length) {
        const labels = answer.optionIds.map(optionId => {
            const option = question?.options?.find(candidate => candidate.id === optionId);
            return ctx.localize(option?.label)?.trim() || optionId;
        });
        parts.push(plainPart(labels.join(', ')));
    }

    if (answer.customText?.trim()) parts.push(fieldPart(answer.customText, SurveyRedactionField.CUSTOM_TEXT));
    if (answer.textValue?.trim()) parts.push(fieldPart(answer.textValue, SurveyRedactionField.TEXT_VALUE));
    if (answer.scaleValue !== undefined && answer.scaleValue !== null) {
        parts.push(ratingPart(answer.scaleValue, answer, question));
    }
    if (answer.rateValue !== undefined && answer.rateValue !== null) {
        parts.push(ratingPart(answer.rateValue, answer, question));
    }
    if (answer.numberValue !== undefined && answer.numberValue !== null) {
        parts.push(plainPart(String(answer.numberValue)));
    }

    if (answer.dateValue) {
        const parsed = convertLocalDateToDate(answer.dateValue);
        parts.push(plainPart(parsed ? formatFullDMY(parsed) : answer.dateValue));
    }

    if (answer.booleanValue !== undefined && answer.booleanValue !== null) {
        parts.push(plainPart(answer.booleanValue ? ctx.t('Yes') : ctx.t('No')));
    }

    return {
        parts,
        mediaCount: answer.mediaValues?.length ?? 0,
        hasSignature: !!answer.signatureValue,
    };
};

export const SURVEY_ANSWER_PART_SEPARATOR = ' · ';

/**
 * The answer as one string, for the spreadsheet exports: the chosen and typed values, "Signed" for a signature, then
 * the link of every upload and of the signature, one per line.
 */
export const surveyAnswerToText = (
    answer: SurveyAnswer | undefined,
    question: SurveyQuestion | undefined,
    ctx: SurveyAnswerContext
): string => {
    const {parts, hasSignature} = surveyAnswerParts(answer, question, ctx);

    const values = [
        ...parts.map(part => part.text),
        hasSignature ? ctx.t('Signed') : undefined,
    ].filter(Boolean).join(SURVEY_ANSWER_PART_SEPARATOR);

    return [values, ...surveyAnswerMediaLinks(answer)].filter(Boolean).join('\n');
};

interface SurveyAnswerValueProps {
    answer: SurveyAnswer | undefined
    question: SurveyQuestion | undefined
    ctx: SurveyAnswerContext
    asPrint?: boolean
}

export function SurveyAnswerValue({answer, question, ctx, asPrint = false}: SurveyAnswerValueProps) {
    if (asPrint) {
        return <>{surveyAnswerToText(answer, question, ctx)}</>;
    }

    const {parts, mediaCount, hasSignature} = surveyAnswerParts(answer, question, ctx);

    if (!parts.length && !mediaCount && !hasSignature) {
        return null;
    }

    return (
        <Stack gap={1.5} sx={{minWidth: 0}}>
            {!!parts.length && (
                <Box sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {parts.map(part => part.text).join(SURVEY_ANSWER_PART_SEPARATOR)}
                </Box>
            )}

            <SurveyAnswerMedia answer={answer} ctx={ctx}/>
        </Stack>
    );
}
